"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { memo } from "react";
import { cn } from "@/lib/utils";
import { getChainInfo } from "./types";

// Chain-specific icon components
const EthereumIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 32 32">
    <path d="M16 32C7.163 32 0 24.837 0 16S7.163 0 16 0s16 7.163 16 16-7.163 16-16 16zm7.994-15.781L16.498 4 9 16.22l7.498 4.353 7.496-4.354zM24 17.616l-7.502 4.351L9 17.617l7.498 10.378L24 17.616z" />
  </svg>
);

const BaseIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="14" />
  </svg>
);

const OptimismIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="14" />
  </svg>
);

const ArbitrumIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="14" />
  </svg>
);

const PolygonIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 32 32">
    <path d="M21.092 13.118l5.296-3.062a1.853 1.853 0 011.854 0l5.296 3.062c.574.332.926.946.926 1.614v6.123c0 .668-.352 1.282-.926 1.614l-5.296 3.062a1.853 1.853 0 01-1.854 0l-5.296-3.062a1.854 1.854 0 01-.926-1.614v-6.123c0-.668.352-1.282.926-1.614z" />
  </svg>
);

const GenericChainIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

const ChainIcon = ({
  chainId,
  className,
}: {
  chainId: string;
  className?: string;
}) => {
  switch (chainId) {
    case "1":
      return <EthereumIcon className={className} />;
    case "137":
      return <PolygonIcon className={className} />;
    case "8453":
      return <BaseIcon className={className} />;
    case "10":
      return <OptimismIcon className={className} />;
    case "42161":
      return <ArbitrumIcon className={className} />;
    default:
      return <GenericChainIcon className={className} />;
  }
};

const chainBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full font-medium transition-colors",
  {
    variants: {
      variant: {
        default: "border bg-background",
        filled: "text-white",
        subtle: "bg-opacity-10",
        outline: "border-2 bg-transparent",
      },
      size: {
        sm: "px-2 py-0.5 text-[10px]",
        md: "px-2.5 py-1 text-xs",
        lg: "px-3 py-1.5 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export type ChainBadgeProps = VariantProps<typeof chainBadgeVariants> & {
  /** Chain ID to display */
  chainId: string;
  /** Whether to show the chain icon */
  showIcon?: boolean;
  /** Whether to show the full name or short name */
  showFullName?: boolean;
  /** Additional CSS classes */
  className?: string;
};

const PureChainBadge = ({
  chainId,
  variant,
  size,
  showIcon = true,
  showFullName = false,
  className,
}: ChainBadgeProps) => {
  const chain = getChainInfo(chainId);

  const iconSize = {
    sm: "size-3",
    md: "size-3.5",
    lg: "size-4",
  }[size ?? "md"];

  // Apply chain-specific colors based on variant
  const getChainStyles = () => {
    switch (variant) {
      case "filled":
        return { backgroundColor: chain.color };
      case "subtle":
        return {
          backgroundColor: `${chain.color}15`,
          color: chain.color,
        };
      case "outline":
        return {
          borderColor: chain.color,
          color: chain.color,
        };
      default:
        return {
          borderColor: `${chain.color}40`,
          color: chain.color,
        };
    }
  };

  return (
    <span
      className={cn(chainBadgeVariants({ variant, size }), className)}
      style={getChainStyles()}
    >
      {showIcon && (
        <ChainIcon
          chainId={chainId}
          className={cn(iconSize, variant === "filled" && "text-white")}
        />
      )}
      <span>{showFullName ? chain.name : chain.shortName}</span>
    </span>
  );
};

export const ChainBadge = memo(PureChainBadge);

/**
 * Status indicator that shows if a transaction succeeded or failed
 */
export type TransactionStatusBadgeProps = {
  status: "success" | "failed" | "pending";
  size?: "sm" | "md" | "lg";
  className?: string;
};

const statusConfig = {
  success: {
    label: "Success",
    className: "bg-accent/10 text-accent border-accent/30",
  },
  failed: {
    label: "Failed",
    className: "bg-destructive/10 text-destructive border-destructive/30",
  },
  pending: {
    label: "Pending",
    className: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  },
};

export const TransactionStatusBadge = memo(function TransactionStatusBadge({
  status,
  size = "md",
  className,
}: TransactionStatusBadgeProps) {
  const config = statusConfig[status];

  const sizeClasses = {
    sm: "px-1.5 py-0.5 text-[10px]",
    md: "px-2 py-0.5 text-xs",
    lg: "px-2.5 py-1 text-sm",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-medium",
        sizeClasses[size],
        config.className,
        className
      )}
    >
      {status === "pending" && (
        <span className="size-1.5 animate-pulse rounded-full bg-current" />
      )}
      {config.label}
    </span>
  );
});
